import {ProductionTipItem, QuerySampleGroup} from './types';

export const SALES_PAGE_INTRO: ProductionTipItem = {
    id: 'sales-intro',
    title: 'Searching sales conversations',
    body: 'Sales queries usually combine what the customer wants (upgrade, price, cancellation) with how they say it (exact phrases and wildcard stems). Use event_agent_text or event_customer_text when you need to know who said it. Add exclusions to remove support-only noise.',
};

export const SALES_SAMPLE_GROUPS: QuerySampleGroup[] = [
    {
        id: 'customer-intent',
        title: 'Customer interest and intent',
        items: [
            {
                id: 'upgrade-interest',
                title: 'Upgrade or plan interest',
                query: '(upgrade* OR "new plan" OR "better package" OR "higher tier") AND (interested OR curious OR "tell me more" OR "what options")',
                description: 'Customer exploring a better plan or add-on.',
            },
            {
                id: 'pricing-inquiry',
                title: 'Pricing inquiry',
                query: '(price* OR cost* OR "how much" OR "monthly fee") AND (plan* OR package* OR bundle* OR subscription*)',
                description: 'Customer asking what something costs before committing.',
            },
            {
                id: 'comparison-shopping',
                title: 'Comparison and deal seeking',
                query: '"thinking about switching"~2 OR "looking for a deal" OR compare* OR "what do you offer"',
                description: 'Customer comparing providers or looking for a better offer.',
            },
            {
                id: 'product-interest',
                title: 'Specific product interest',
                query: '(fiber* OR broadband* OR mobile* OR tv*) AND (interested OR available OR "do you have" OR "can I get")',
                description: 'Customer asking about a particular product line.',
            },
        ],
    },
    {
        id: 'agent-offers',
        title: 'Agent offers and recommendations',
        items: [
            {
                id: 'agent-offer',
                title: 'Agent makes an offer',
                query: 'event_agent_text:(offer* OR recommend* OR suggest*) AND (plan* OR package* OR upgrade* OR bundle*)',
                description: 'Agent-side language scoped to proposals and recommendations.',
            },
            {
                id: 'promotion-language',
                title: 'Promotion and limited-time language',
                query: '"special offer" OR "limited time" OR "promotion available" OR "discounted rate" OR "exclusive deal"',
                description: 'Marketing or promotional framing in the conversation.',
            },
            {
                id: 'upsell-crosssell',
                title: 'Upsell and cross-sell',
                query: '(add* OR include* OR bundle*) AND (plan* OR service* OR feature*) AND event_agent_text:(recommend* OR suggest* OR offer*)',
                description: 'Agent suggesting an additional product on top of the current one.',
            },
            {
                id: 'value-proposition',
                title: 'Value proposition',
                query: '"save money"~2 OR "better value" OR "more for your money" OR "lower monthly" OR "includes everything"',
                description: 'Agent or customer discussing value rather than pure price.',
            },
        ],
    },
    {
        id: 'objections',
        title: 'Objections and hesitation',
        items: [
            {
                id: 'price-objection',
                title: 'Price objection',
                query: '(expensive OR "too much" OR costly OR "cannot afford") AND (plan* OR price* OR bill* OR monthly*)',
                description: 'Customer pushing back on cost.',
            },
            {
                id: 'stalling',
                title: 'Stalling and deferral',
                query: '"need to think" OR "talk to spouse" OR "talk to my partner" OR "call back later" OR "not sure yet" OR "need time to decide"',
                description: 'Customer delaying a decision.',
            },
            {
                id: 'competitor-mention',
                title: 'Competitor mention',
                query: '(competitor* OR rival* OR "other provider") OR "better deal elsewhere" OR "cheaper at" OR "they offered me"',
                description: 'Customer referencing an alternative provider.',
            },
            {
                id: 'trust-objection',
                title: 'Trust or commitment objection',
                query: '"not ready" OR "do not want to commit" OR "long contract" OR "lock me in" OR "hidden fees"',
                description: 'Hesitation about terms, commitment length, or transparency.',
            },
        ],
    },
    {
        id: 'closing',
        title: 'Closing and acceptance',
        items: [
            {
                id: 'verbal-yes',
                title: 'Verbal acceptance',
                query: '"ready to sign" OR "let\'s do it" OR "go ahead" OR "I accept" OR "sounds good" OR "that works for me"',
                description: 'Customer agreeing to move forward.',
            },
            {
                id: 'order-confirmation',
                title: 'Order and subscription confirmation',
                query: '(order* OR subscribe* OR sign*) AND (confirm* OR proceed* OR "move forward" OR "set it up")',
                description: 'Language around confirming or starting an order.',
            },
            {
                id: 'contract-next-steps',
                title: 'Contract and next steps',
                query: '"send me the contract" OR "email the agreement" OR "start the order" OR "when does it activate"',
                description: 'Customer asking for paperwork or activation timing.',
            },
        ],
    },
    {
        id: 'retention',
        title: 'Retention and churn risk',
        items: [
            {
                id: 'cancellation-intent',
                title: 'Cancellation intent',
                query: '(cancel* OR terminate* OR disconnect*) AND (account* OR subscription* OR service* OR contract*)',
                description: 'Customer wanting to end service.',
            },
            {
                id: 'switching-away',
                title: 'Switching provider',
                query: '"thinking of leaving"~2 OR "switch provider" OR "end my contract" OR "move to another company"',
                description: 'Churn risk language before a formal cancel request.',
            },
            {
                id: 'retention-save',
                title: 'Retention save attempt',
                query: '(retention OR save OR "stay with us" OR "keep your service") AND (offer* OR discount* OR "better price" OR "match the price")',
                description: 'Agent trying to retain the customer with an offer.',
            },
            {
                id: 'winback',
                title: 'Win-back and return',
                query: '(return* OR come back OR reactivate*) AND (customer* OR account* OR subscription*)',
                description: 'Conversation about returning after cancellation or lapse.',
            },
        ],
    },
    {
        id: 'promotions',
        title: 'Discounts and promotions',
        items: [
            {
                id: 'discount-eligibility',
                title: 'Discount eligibility',
                query: '(discount* OR promo* OR coupon* OR rebate*) AND (apply* OR eligible* OR available* OR qualify*)',
                description: 'Discussion of whether a promotion applies.',
            },
            {
                id: 'fee-waiver',
                title: 'Waived fees',
                query: '(free* OR waived* OR "no charge") AND (install* OR activation* OR setup* OR "connection fee")',
                description: 'Negotiation around one-time or setup costs.',
            },
            {
                id: 'loyalty-benefit',
                title: 'Loyalty and tenure benefit',
                query: '"loyalty discount" OR "long-term customer"~2 OR "been with you for" OR "existing customer offer"',
                description: 'Customer invoking tenure or loyalty for a better deal.',
            },
        ],
    },
    {
        id: 'production-style',
        title: 'Production-style sales query',
        items: [
            {
                id: 'sales-with-exclusions',
                title: 'Upgrade interest minus support noise',
                query: '(upgrade* OR "new plan" OR "better package") AND (interested OR price* OR "how much" OR "tell me more") -support* -technical* -outage* -broken*',
                description: 'Sales intent with exclusions for technical support conversations.',
            },
            {
                id: 'sales-phrase-bank',
                title: 'Sales opportunity phrase bank',
                query: '("interested in upgrading"~2 "looking at plans" "what packages do you have" "can you match"~3 "best deal" "monthly price" "sign up today"~2 upgrade* bundle* promo*) -cancel* -complaint*',
                description: 'Phrase bank pattern for broad sales opportunity recall, excluding cancel/complaint language.',
            },
            {
                id: 'retention-production',
                title: 'Retention save (production shape)',
                query: '(cancel* OR "want to leave" OR "switch provider"~2) AND (retention OR save OR discount* OR "better offer" OR "match price"~2 OR "stay with us")',
                description: 'Combines churn signals with retention offer language in one query.',
            },
        ],
    },
];
